import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Target, Ruler, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUserTrailPreferences, TrailPreferenceWithTrail } from "@/hooks/useTrailPreferences";
import { ROUTES } from "@/lib/routes";

interface WantToGoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const WantToGoDialog = ({ open, onOpenChange }: WantToGoDialogProps) => {
  const navigate = useNavigate();
  const { wantToGo, isLoading } = useUserTrailPreferences();

  const handleTrailClick = (trailId: string) => {
    onOpenChange(false);
    navigate(ROUTES.trail(trailId));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Trail Wishlist
          </DialogTitle>
          <DialogDescription>
            Trails you want to explore
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
              ))}
            </div>
          ) : wantToGo.length === 0 ? (
            <div className="text-center py-8">
              <Target className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No trails in your wishlist yet</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => {
                  onOpenChange(false);
                  navigate("/explore");
                }}
              >
                Explore Trails
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {wantToGo.map((pref: TrailPreferenceWithTrail) => (
                <button
                  key={pref.id}
                  onClick={() => handleTrailClick(pref.trail_id)}
                  className="w-full p-3 rounded-lg border bg-card hover:bg-accent transition-colors text-left flex items-center justify-between group"
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">
                      {pref.trails?.name || "Unknown Trail"}
                    </h4>
                    {pref.trails?.length_miles && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <Ruler className="h-3 w-3" />
                        <span>{pref.trails.length_miles.toFixed(1)} mi</span>
                      </div>
                    )}
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
